import { useState } from "react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { EntityName } from "@/components/cinch/EntityName";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/lib/auth";

import { useAddWatch, useRemoveWatch, useWatchlist, type WatchOut } from "../api";
import { LEVEL_LABEL, watchContext, watchName } from "../display";

/** Watch any L1/L2/L3 key: it gets scored nightly with the same gates (lower
 *  floors) and may open incidents at its own level. Rows drill into the
 *  entity drawer with the standard dual-clock chart. */
export function WatchlistTab({
  onOpenEntity,
}: {
  onOpenEntity: (level: string, keyPath: string) => void;
}) {
  const canWrite = useCan("integrations.write");
  const watchlist = useWatchlist();
  const addWatch = useAddWatch();
  const removeWatch = useRemoveWatch();
  const [level, setLevel] = useState("L1");
  const [keyPath, setKeyPath] = useState("");
  const [reasonText, setReasonText] = useState("");

  const columns: DataTableColumn<WatchOut>[] = [
    {
      key: "key_path",
      header: "Watching",
      render: (r) => (
        <EntityName
          name={watchName(r)}
          fallback={r.key_path}
          context={watchContext(r)}
          badge={<StatusBadge value={r.level} label={LEVEL_LABEL[r.level] ?? r.level} />}
        />
      ),
    },
    { key: "reason", header: "Reason", render: (r) => r.reason ?? "—" },
    { key: "added_by_name", header: "Added by", render: (r) => r.added_by_name ?? "—" },
    { key: "expires_at", header: "Expires", render: (r) => r.expires_at ?? "—" },
    {
      key: "_actions",
      header: "",
      render: (r) =>
        canWrite ? (
          <Button
            variant="outline"
            size="sm"
            disabled={removeWatch.isPending}
            onClick={(e) => {
              e.stopPropagation();
              removeWatch.mutate(r.id);
            }}
          >
            Remove
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        This is one shared list for all of CinchOps — everyone sees the same entries,
        and each shows who added it and why.
      </p>
      {canWrite && (
        <Card className="flex flex-wrap items-end gap-2 p-3">
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Level</p>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="h-9 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="L1">L1 — integration</SelectItem>
                <SelectItem value="L2">L2 — company</SelectItem>
                <SelectItem value="L3">L3 — location</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-64 flex-1">
            <p className="mb-1 text-xs text-muted-foreground">
              Key path (source, source/company, source/company/location)
            </p>
            <Input
              value={keyPath}
              onChange={(e) => setKeyPath(e.target.value)}
              placeholder="grow or grow/COMPANY_ID"
            />
          </div>
          <div className="min-w-48 flex-1">
            <p className="mb-1 text-xs text-muted-foreground">Reason</p>
            <Input
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Why watch this?"
            />
          </div>
          <Button
            disabled={!keyPath.trim() || addWatch.isPending}
            onClick={() =>
              addWatch.mutate(
                { level, key_path: keyPath.trim(), reason: reasonText.trim() || undefined },
                { onSuccess: () => { setKeyPath(""); setReasonText(""); } },
              )
            }
          >
            Watch
          </Button>
        </Card>
      )}
      <DataTable
        columns={columns}
        rows={watchlist.data ?? []}
        rowKey={(r) => r.id}
        loading={watchlist.isLoading}
        onRowClick={(r) => onOpenEntity(r.level, r.key_path)}
        emptyMessage="Nothing watched. Watch a company or location to score it nightly."
      />
    </div>
  );
}
